import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  ArrowLeft, Search, Copy, ExternalLink, Pencil, Trash2,
  Users, Loader2, FileText, X, Check, ChevronDown, ChevronRight,
  LinkIcon, Download, Clock, Mail, Sparkles,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NotificationsBell } from "@/components/NotificationsBell";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) =>
  v != null
    ? new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(v)
    : "—";

const REGIME_LABELS = {
  contract: "Contrato",
  green_receipts: "Recibos Verdes",
  b2b: "B2B",
};

// Detecta o tipo completo (Outsourcing + regime, ou Perm) e devolve label + classes Tailwind
const getBusinessType = (sim) => {
  if (sim?.simulation_data?.business_type === "perm") {
    return { key: "perm", label: "Perm", color: "bg-purple-50 text-purple-700 border-purple-200" };
  }
  const r = sim?.simulation_data?.regime_type || "contract";
  if (r === "green_receipts") return { key: "green_receipts", label: "Recibos Verdes", color: "bg-amber-50 text-amber-700 border-amber-200" };
  if (r === "b2b") return { key: "b2b", label: "B2B", color: "bg-indigo-50 text-indigo-700 border-indigo-200" };
  return { key: "contract", label: "Outsourcing — Contrato", color: "bg-blue-50 text-blue-700 border-blue-200" };
};

const CandidateLinks = () => {
  const { getAuthHeaders, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterNif, setFilterNif] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", nif: "", birth_date: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [expandedCandidates, setExpandedCandidates] = useState({});
  const [candidateProposals, setCandidateProposals] = useState({});
  const [sortBy, setSortBy] = useState("recent"); // recent | oldest | name
  const autoSaveTimer = useRef(null);

  // Email dialog state
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailProposal, setEmailProposal] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  // Create-link confirmation dialog state. We never create the proposal silently
  // — the user must explicitly click "Guardar" inside this dialog.
  const [linkDialog, setLinkDialog] = useState({ open: false, sim: null, group: null, key: null, saving: false });
  const openLinkDialog = (sim, group, key) => {
    // Strict guard: prevent creating proposal links from simulations whose
    // computed margin is negative. The user must re-open the simulation in the
    // simulator and adjust it until the margin is ≥ 0%.
    const sr = sim?.results?.results?.[0];
    const marginPct = sr?.margin_percentage;
    if (typeof marginPct === "number" && marginPct < 0) {
      toast.error(
        `Não é possível criar link com margem negativa (${marginPct.toFixed(1)}%). Ajusta a simulação até obteres margem ≥ 0%.`,
        { duration: 6000 }
      );
      return;
    }
    setLinkDialog({ open: true, sim, group, key, saving: false });
  };
  const closeLinkDialog = () => {
    setLinkDialog({ open: false, sim: null, group: null, key: null, saving: false });
  };

  const loadSimulations = useCallback(async (q = "", extra = {}) => {
    try {
      const params = {};
      if (q) params.q = q;
      if (extra.email) params.email = extra.email;
      if (extra.nif) params.nif = extra.nif;
      if (extra.date_from) params.date_from = extra.date_from;
      if (extra.date_to) params.date_to = extra.date_to;
      const res = await axios.get(`${API}/simulations-all`, {
        headers: getAuthHeaders(),
        params,
      });
      setSimulations(res.data);
    } catch {
      toast.error("Erro ao carregar simulações");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // eslint-disable-next-line
  useEffect(() => { loadSimulations(); }, [loadSimulations]);

  // Debounce filter changes — store the snapshot in state, then load on snapshot change
  const [debouncedFilters, setDebouncedFilters] = useState({
    q: "", email: "", nif: "", date_from: "", date_to: "",
  });
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters({
        q: searchQuery,
        email: filterEmail,
        nif: filterNif,
        date_from: filterDateFrom,
        date_to: filterDateTo,
      });
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery, filterEmail, filterNif, filterDateFrom, filterDateTo]);
  useEffect(() => {
    // eslint-disable-next-line
    loadSimulations(debouncedFilters.q, debouncedFilters);
  }, [debouncedFilters, loadSimulations]);

  // Group simulations by Name first (most stable identifier across simulations
  // for the same candidate, where NIF/email are often optional), then fallback
  // to NIF, Email or sim.id. Normalize values (trim + lowercase) so trivial
  // whitespace/case differences do not create duplicate cards.
  const grouped = simulations.reduce((acc, sim) => {
    const nif = (sim.nif || "").trim();
    const email = (sim.email || "").trim().toLowerCase();
    const name = (sim.name || "").trim().toLowerCase();
    const key = name
      ? `name:${name}`
      : nif
      ? `nif:${nif}`
      : email
      ? `email:${email}`
      : `id:${sim.id}`;
    if (!acc[key]) acc[key] = { name: sim.name, nif: sim.nif || "", email: sim.email || "", simulations: [] };
    if (sim.name && !acc[key].name) acc[key].name = sim.name;
    if (sim.nif && !acc[key].nif) acc[key].nif = sim.nif;
    if (sim.email && !acc[key].email) acc[key].email = sim.email;
    acc[key].simulations.push(sim);
    return acc;
  }, {});

  // Sort simulations within each group + sort groups themselves by sortBy
  const _latestDate = (group) => {
    const dates = group.simulations.map(s => new Date(s.updated_at || s.created_at || 0).getTime());
    return Math.max(...dates, 0);
  };
  Object.values(grouped).forEach(g => {
    g.simulations.sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0));
  });
  const groupedEntries = Object.entries(grouped).sort(([, a], [, b]) => {
    if (sortBy === "oldest") return _latestDate(a) - _latestDate(b);
    if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
    return _latestDate(b) - _latestDate(a); // recent (default)
  });

  const toggleExpand = async (key, group) => {
    const isOpen = expandedCandidates[key];
    setExpandedCandidates(prev => ({ ...prev, [key]: !isOpen }));
    if (!isOpen && !candidateProposals[key]) {
      try {
        const res = await axios.get(`${API}/proposals/by-candidate`, {
          headers: getAuthHeaders(),
          params: { nif: group.nif || "", email: group.email || "" }
        });
        setCandidateProposals(prev => ({ ...prev, [key]: res.data }));
      } catch { /* swallow */ }
    }
  };

  const openEdit = (sim) => {
    setEditingItem(sim);
    setEditForm({
      name: sim.name || "",
      nif: sim.nif || sim.custom_id || "",
      birth_date: sim.birth_date || "",
      notes: sim.notes || "",
    });
    setSaved(false);
    setEditDialog(true);
  };

  const autoSaveCandidate = useCallback(async (form) => {
    if (!editingItem) return;
    setSaving(true);
    setSaved(false);
    try {
      await axios.put(`${API}/simulations/${editingItem.id}/candidate`, form, { headers: getAuthHeaders() });
      setSaved(true);
      setSimulations((prev) =>
        prev.map((s) =>
          s.id === editingItem.id
            ? { ...s, name: form.name, nif: form.nif, birth_date: form.birth_date, notes: form.notes }
            : s
        )
      );
    } catch {
      toast.error("Erro ao guardar");
    } finally {
      setSaving(false);
    }
  }, [editingItem, getAuthHeaders]);

  const updateEditForm = (newForm) => {
    setEditForm(newForm);
    setSaved(false);
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => autoSaveCandidate(newForm), 800);
  };

  const saveCandidate = async () => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    await autoSaveCandidate(editForm);
  };

  const deleteSimulation = async (sim) => {
    if (!window.confirm(`Eliminar a simulação de "${sim.name || sim.id}"?`)) return;
    try {
      await axios.delete(`${API}/simulations/${sim.id}`, { headers: getAuthHeaders() });
      setSimulations((prev) => prev.filter((s) => s.id !== sim.id));
      toast.success("Simulação eliminada");
    } catch {
      toast.error("Erro ao eliminar");
    }
  };

  // Multi-select state for bulk delete
  const [selectedSimIds, setSelectedSimIds] = useState(new Set());
  const toggleSimSelected = (simId) => {
    setSelectedSimIds((prev) => {
      const next = new Set(prev);
      if (next.has(simId)) next.delete(simId); else next.add(simId);
      return next;
    });
  };
  // Toggle ALL simulations of a candidate (group-level checkbox). When every
  // sim in the group is already selected, deselect them; otherwise select all
  // that are not yet selected.
  const groupSelectionState = (group) => {
    const ids = group.simulations.map(s => s.id);
    const total = ids.length;
    const selected = ids.filter(id => selectedSimIds.has(id)).length;
    if (selected === 0) return "none";
    if (selected === total) return "all";
    return "some";
  };
  const toggleGroupSelected = (group) => {
    setSelectedSimIds((prev) => {
      const next = new Set(prev);
      const ids = group.simulations.map(s => s.id);
      const allSelected = ids.every(id => next.has(id));
      if (allSelected) {
        ids.forEach(id => next.delete(id));
      } else {
        ids.forEach(id => next.add(id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedSimIds(new Set());
  const deleteSelectedSims = async () => {
    if (selectedSimIds.size === 0) return;
    if (!window.confirm(`Eliminar ${selectedSimIds.size} simulação(ões) seleccionada(s)? Esta ação é irreversível.`)) return;
    const ids = Array.from(selectedSimIds);
    let okCount = 0; let failCount = 0;
    await Promise.all(ids.map(async (id) => {
      try {
        await axios.delete(`${API}/simulations/${id}`, { headers: getAuthHeaders() });
        okCount++;
      } catch {
        failCount++;
      }
    }));
    setSimulations((prev) => prev.filter((s) => !selectedSimIds.has(s.id)));
    clearSelection();
    if (failCount === 0) toast.success(`${okCount} simulação(ões) eliminada(s)`);
    else toast.warning(`${okCount} eliminadas, ${failCount} falharam`);
  };

  const copyLink = (url) => {
    try { navigator.clipboard.writeText(url); toast.success("Link copiado (não foi enviado por email)"); } catch { /* swallow */ }
  };

  const getResult = (sim) => sim.results?.results?.[0] || null;
  const getRegime = (sim) => sim.simulation_data?.regime_type || "contract";

  // Build a human-friendly rate label (e.g. "200,00 €/dia" or "25,00 €/h" or
  // "2.500,00 €/mês") from the simulation_data. Returns null when there's no
  // rate (e.g. PERM regime where the input is salary, not a billable rate).
  // Fix 2026-02-25: quando o negócio é Fix Fee (is_fix_fee) ou o `client_rate`
  // está vazio, usa-se o `result.rate_monthly` já calculado pelo backend.
  const getRateLabel = (sim) => {
    const sd = sim?.simulation_data || {};
    if (sd.business_type === "perm") return null;
    const rate = parseFloat(sd.client_rate);
    const isFixFee = !!sd.is_fix_fee;
    const rateEmpty = !rate || isNaN(rate);
    if (isFixFee || rateEmpty) {
      // Extrai o rate_monthly do resultado (pode vir directamente ou dentro de results[0]).
      const res = sim?.results;
      const r0 = Array.isArray(res) ? res[0] : (res?.results?.[0] || res);
      const monthly = parseFloat(r0?.rate_monthly);
      if (!monthly || isNaN(monthly)) return null;
      const formatted = new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(monthly);
      return `${formatted}/mês`;
    }
    const t = sd.client_rate_type || "daily";
    const suffix = t === "hourly" ? "/h" : t === "monthly" ? "/mês" : "/dia";
    const formatted = new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(rate);
    return `${formatted}${suffix}`;
  };

  // Compute liquid values (Líq.14 e Líq.12) — fonte de verdade = backend fields
  // `liq_14_monthly` e `liq_12_monthly` (expostos em 2026-02-25). Fallback para
  // simulações antigas: deriva via aproximação do `net_monthly`.
  const getLiquids = (r) => {
    if (!r) return { liq14: null, liq12: null };
    if (r.liq_14_monthly != null && r.liq_12_monthly != null) {
      return {
        liq14: Number(r.liq_14_monthly) || 0,
        liq12: Number(r.liq_12_monthly) || 0,
      };
    }
    // Fallback (simulações antigas sem os campos directos)
    const netMonthlyBackend = Number(r.net_monthly) || 0;
    const gross = r.gross_monthly || 0;
    const irs = r.irs_retention || 0;
    const ss = r.social_security_employee || 0;
    const subsidyNet = gross - irs - ss;
    const liq12 = netMonthlyBackend;
    const liq14 = netMonthlyBackend - (subsidyNet * 2) / 12;
    return { liq14, liq12 };
  };

  const exportCandidate = async (group) => {
    // Exporta o candidato como PDF, usando a simulação mais recente.
    // Antes exportava JSON (dev-only); agora entrega o mesmo PDF de proposta
    // que o `Gerar PDF` do link partilhado usa (`/api/proposal/generate-pdf`).
    const latestSim = group.simulations?.[0];
    if (!latestSim) {
      toast.error("Sem simulações para exportar");
      return;
    }
    const results = latestSim.results && !Array.isArray(latestSim.results)
      ? latestSim.results
      : { results: Array.isArray(latestSim.results) ? latestSim.results : [] };
    if (!results?.results?.length) {
      toast.error("Simulação sem resultados calculados");
      return;
    }

    const t = toast.loading("A gerar PDF…");
    try {
      // Vai buscar defaults dos textos/company (Backoffice) para o header do PDF.
      let settings = { texts: {}, company: {} };
      try {
        const s = await axios.get(`${API}/proposal-settings`, { headers: getAuthHeaders() });
        settings = s.data || settings;
      } catch { /* fallback aos defaults */ }
      const texts = settings.texts || {};
      const company = settings.company || {};

      const payload = {
        consultant_name: group.name || latestSim.name || "Candidato",
        consultant_nif: group.nif || latestSim.nif || "",
        consultant_function: latestSim.simulation_data?.consultant_function || "",
        simulation_data: latestSim.simulation_data || {},
        results: results,
        ...texts,
        cover_title: texts.cover_title || "Proposta Financeira",
        cover_subtitle: texts.cover_subtitle || "",
        greeting_text: texts.greeting_text || "",
        motivational_text: texts.motivational_text || "",
        annual_title: texts.annual_title || "Visão Anual Detalhada",
        annual_subtitle: texts.annual_subtitle || "",
        thanks_title: texts.thanks_title || "Obrigado!",
        thanks_text: texts.thanks_text || "",
        other_benefits_title: texts.other_benefits_title || "Benefícios",
        other_benefits_text: texts.other_benefits_text || "",
        footer_text: texts.footer_text || "",
        company_name: company.company_name || "Evolutt",
        company_slogan: company.company_slogan || "Unlocking What's Next",
        company_address: company.company_address || "",
        company_postal_code: company.company_postal_code || "",
        company_city: company.company_city || "",
        company_phone1: company.company_phone1 || "",
        company_phone2: company.company_phone2 || "",
        company_website: company.company_website || "",
        company_email: company.company_email || "",
        health_insurance_name: texts.health_insurance_name || "",
      };

      const r = await axios.post(`${API}/proposal/generate-pdf`, payload, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });
      if (!r.data || r.data.size === 0) {
        toast.error("PDF vazio", { id: t });
        return;
      }
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (group.name || group.nif || "candidato").replace(/\s+/g, "_");
      a.download = `Proposta_${safe}_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 100);
      toast.success("PDF exportado!", { id: t });
    } catch (err) {
      const msg = err?.response?.status
        ? `Erro ${err.response.status}: ${err.response.statusText || "ao gerar PDF"}`
        : err?.message || "Erro ao gerar PDF";
      toast.error(msg, { id: t });
    }
  };

  const downloadProposalPdf = async (proposalId) => {
    try {
      const res = await axios.post(`${API}/proposals/${proposalId}/generate-signed-pdf`, {}, {
        headers: getAuthHeaders(),
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `proposta_${proposalId.substring(0,8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("PDF gerado!");
    } catch {
      toast.error("Erro ao gerar PDF");
    }
  };

  // Get current proposal settings (texts + company) to build PDF / proposal
  const getProposalSettings = async () => {
    try {
      const res = await axios.get(`${API}/proposal-settings`, { headers: getAuthHeaders() });
      return res.data || {};
    } catch {
      return {};
    }
  };

  // Generate PDF directly from a simulation (without saving as proposal)
  const generatePdfFromSimulation = async (sim, group) => {
    try {
      const settings = await getProposalSettings();
      const texts = settings.texts || {};
      const company = settings.company || {};
      const payload = {
        consultant_name: group.name || sim.name || "Candidato",
        consultant_nif: group.nif || "",
        consultant_function: sim.simulation_data?.consultant_function || "",
        ...texts,
        cover_title: texts.cover_title || "Proposta Financeira",
        cover_subtitle: texts.cover_subtitle || "",
        greeting_text: "",
        motivational_text: texts.motivational_text || "",
        annual_title: texts.annual_title || "Visão Anual Detalhada",
        annual_subtitle: texts.annual_subtitle || "",
        thanks_title: texts.thanks_title || "Obrigado!",
        thanks_text: texts.thanks_text || "",
        other_benefits_title: texts.other_benefits_title || "Benefícios",
        other_benefits_text: texts.other_benefits_text || "",
        footer_text: texts.footer_text || "",
        company_name: company.company_name || "Evolutt",
        company_slogan: company.company_slogan || "Unlocking What's Next",
        company_address: company.company_address || "",
        company_postal_code: company.company_postal_code || "",
        company_city: company.company_city || "",
        company_phone1: company.company_phone1 || "",
        company_phone2: company.company_phone2 || "",
        company_website: company.company_website || "",
        company_email: company.company_email || "",
        health_insurance_name: texts.health_insurance_name || "",
        simulation_data: sim.simulation_data || {},
        results: sim.results || {},
      };
      const res = await axios.post(`${API}/proposal/generate-pdf`, payload, {
        headers: getAuthHeaders(),
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const safe = (group.name || "candidato").replace(/\s+/g, "_");
      a.download = `Proposta_${safe}_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("PDF gerado!");
    } catch {
      toast.error("Erro ao gerar PDF");
    }
  };

  // Open email dialog for a given proposal (with prefilled recipient if available)
  const openEmailDialog = (proposal, prefillEmail = "") => {
    setEmailProposal(proposal);
    setEmailTo(prefillEmail || proposal.candidate_email || "");
    setEmailDialog(true);
  };

  // Send proposal via Resend (link + PDF attached)
  const sendProposalEmail = async () => {
    if (!emailProposal) return;
    const to = (emailTo || "").trim();
    if (!to || !to.includes("@")) {
      toast.error("Email inválido");
      return;
    }
    setEmailSending(true);
    try {
      const res = await axios.post(
        `${API}/proposals/${emailProposal.id}/send-email`,
        { to, origin: window.location.origin },
        { headers: getAuthHeaders() }
      );
      toast.success(`Email enviado para ${to}`);
      setEmailDialog(false);
      setEmailProposal(null);
      setEmailTo("");
      // Optional: notify on delivery_id if useful
      if (res?.data?.delivery_id) {
        console.log("Resend delivery_id:", res.data.delivery_id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar email");
    } finally {
      setEmailSending(false);
    }
  };

  // Cleanup invalid simulations (margin outside [-100%, +100%]). Admin-only.
  const [cleaningInvalid, setCleaningInvalid] = useState(false);
  const cleanupInvalidSims = async () => {
    if (!window.confirm("Eliminar todas as simulações com margem inválida (< -100% ou > +100%)? Esta ação é irreversível.")) return;
    setCleaningInvalid(true);
    try {
      const res = await axios.post(`${API}/simulations/cleanup-invalid`, {}, { headers: getAuthHeaders() });
      const count = res.data?.deleted_count ?? 0;
      if (count === 0) {
        toast.success("Nenhuma simulação inválida encontrada.");
      } else {
        toast.success(`${count} simulação(ões) inválida(s) eliminada(s).`);
        // Refresh list
        await loadSimulations(debouncedFilters.q, debouncedFilters);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao limpar histórico");
    } finally {
      setCleaningInvalid(false);
    }
  };

  // Bulk refresh — recalcula os `results` de TODAS as simulações activas usando
  // o `simulation_data` guardado. Admin-only. Útil quando taxas/settings mudam
  // e queremos que os links antigos (e a tabela de Candidatos) fiquem alinhados
  // sem depender de uma visita manual a cada link.
  const [refreshingAll, setRefreshingAll] = useState(false);
  const refreshAllSims = async () => {
    if (!window.confirm("Recalcular TODAS as simulações activas com as taxas/settings actuais? Os inputs (nome, NIF, dados) não são alterados; apenas os resultados são recomputados.")) return;
    setRefreshingAll(true);
    try {
      const res = await axios.post(`${API}/simulations/refresh-all`, {}, { headers: getAuthHeaders() });
      const { total = 0, refreshed = 0, failed = 0 } = res.data || {};
      if (failed > 0) {
        toast.warning(`Recalculadas ${refreshed}/${total} simulações. ${failed} falharam (dados incompatíveis).`);
      } else {
        toast.success(`${refreshed} simulação(ões) recalculada(s) com sucesso.`);
      }
      await loadSimulations(debouncedFilters.q, debouncedFilters);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao recalcular simulações");
    } finally {
      setRefreshingAll(false);
    }
  };

  // Create proposal + sharing link from a simulation. Always invoked from the
  // confirmation dialog (never directly from the row), so the user MUST click
  // "Guardar" inside the dialog before the proposal is persisted.
  const confirmCreateProposalLink = async () => {
    const { sim, group, key } = linkDialog;
    if (!sim || !group) return;
    setLinkDialog((s) => ({ ...s, saving: true }));
    try {
      const settings = await getProposalSettings();
      const payload = {
        candidate_name: group.name || sim.name || "Candidato",
        candidate_email: group.email || "",
        candidate_nif: group.nif || "",
        candidate_function: sim.simulation_data?.consultant_function || "",
        simulation_data: sim.simulation_data || {},
        results: sim.results || {},
        proposal_settings: settings,
      };
      const res = await axios.post(`${API}/proposals`, payload, { headers: getAuthHeaders() });
      const link = `${window.location.origin}/proposal/sign/${res.data.token}`;
      try { await navigator.clipboard.writeText(link); } catch { /* swallow */ }
      toast.success("Link guardado e copiado (não foi enviado por email)");
      // Reload proposals for this candidate group
      try {
        const r2 = await axios.get(`${API}/proposals/by-candidate`, {
          headers: getAuthHeaders(),
          params: { nif: group.nif || "", email: group.email || "" }
        });
        setCandidateProposals(prev => ({ ...prev, [key]: r2.data }));
        setExpandedCandidates(prev => ({ ...prev, [key]: true }));
      } catch { /* swallow */ }
      closeLinkDialog();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar link");
      setLinkDialog((s) => ({ ...s, saving: false }));
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="candidate-links-page">
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/simulator">
              <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-2" />Voltar</Button>
            </Link>
            <Users className="w-5 h-5 text-[var(--brand-primary)]" />
            <span className="font-manrope font-bold text-lg">Candidatos & Links</span>
            <span className="text-xs text-muted-foreground bg-slate-100 px-2 py-0.5 rounded">
              {Object.keys(grouped).length} candidatos / {simulations.length} simulações
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={refreshAllSims}
                disabled={refreshingAll}
                className="h-8 px-3 border-brand-300 text-brand-800 hover:bg-brand-50"
                title="Recalcular todos os results com as taxas/settings actuais (admin)"
                data-testid="refresh-all-btn"
              >
                {refreshingAll ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />A recalcular...</>
                ) : (
                  <><Clock className="w-3.5 h-3.5 mr-1.5" />Recalcular tudo</>
                )}
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={cleanupInvalidSims}
                disabled={cleaningInvalid}
                className="h-8 px-3 border-amber-300 text-amber-800 hover:bg-amber-50"
                title="Eliminar simulações com margem fora de [-100%, +100%] (admin)"
                data-testid="cleanup-invalid-btn"
              >
                {cleaningInvalid ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />A limpar...</>
                ) : (
                  <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Limpar inválidas</>
                )}
              </Button>
            )}
            <NotificationsBell />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {selectedSimIds.size > 0 && (
          <div
            className="mb-4 flex items-center justify-between gap-3 p-3 rounded-lg bg-[var(--brand-primary)] text-white shadow-md sticky top-16 z-40"
            data-testid="bulk-action-bar"
          >
            <div className="flex items-center gap-2 text-sm">
              <strong>{selectedSimIds.size}</strong> simulação(ões) seleccionada(s)
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={clearSelection} className="text-white hover:bg-white/10" data-testid="bulk-clear-btn">
                Limpar
              </Button>
              <Button variant="destructive" size="sm" onClick={deleteSelectedSims} data-testid="bulk-delete-btn">
                <Trash2 className="w-3 h-3 mr-1" />
                Eliminar selecção
              </Button>
            </div>
          </div>
        )}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Pesquisar por nome ou NIF..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-11 pr-32"
            data-testid="candidate-search-input"
          />
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs px-2 py-1 rounded ${showFilters ? "bg-[var(--brand-primary)] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid="toggle-filters-btn"
          >
            Filtros {(filterEmail || filterNif || filterDateFrom || filterDateTo) && "•"}
          </button>
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} className="absolute right-24 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6 p-3 bg-slate-50 rounded-lg border" data-testid="advanced-filters">
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Email</Label>
              <Input value={filterEmail} onChange={(e) => setFilterEmail(e.target.value)} placeholder="email@..." className="h-8 text-xs mt-1" data-testid="filter-email-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">NIF</Label>
              <Input value={filterNif} onChange={(e) => setFilterNif(e.target.value)} placeholder="123456789" className="h-8 text-xs mt-1" data-testid="filter-nif-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Desde</Label>
              <Input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="h-8 text-xs mt-1" data-testid="filter-date-from" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Até</Label>
              <Input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="h-8 text-xs mt-1" data-testid="filter-date-to" />
            </div>
            <div className="col-span-2 md:col-span-4 flex justify-end">
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setFilterEmail(""); setFilterNif(""); setFilterDateFrom(""); setFilterDateTo(""); }} data-testid="clear-filters-btn">
                Limpar Filtros
              </Button>
            </div>
          </div>
        )}
        {!showFilters && <div className="mb-6" />}

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[var(--brand-primary)]" /></div>
        ) : Object.keys(grouped).length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>{searchQuery ? "Nenhum resultado encontrado." : "Nenhuma simulação guardada."}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Sort selector */}
            <div className="flex items-center justify-end gap-2 -mt-2 mb-1">
              <Label className="text-[10px] uppercase text-muted-foreground">Ordenar:</Label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-xs h-7 border rounded px-2 bg-white"
                data-testid="candidates-sort-select"
              >
                <option value="recent">Mais recentes</option>
                <option value="oldest">Mais antigos</option>
                <option value="name">Nome (A-Z)</option>
              </select>
            </div>
            {groupedEntries.map(([key, group]) => {
              const isExpanded = expandedCandidates[key];
              const proposals = candidateProposals[key] || [];
              const latestSim = group.simulations[0];
              const r = getResult(latestSim);
              const scoreColor =
                r?.score_color === "green" ? "bg-brand-500" :
                r?.score_color === "lime" ? "bg-lime-400" :
                r?.score_color === "yellow" ? "bg-yellow-500" :
                r?.score_color === "orange" ? "bg-orange-500" :
                r?.score_color === "red" ? "bg-red-500" : "bg-slate-300";

              return (
                <Card key={key} className="overflow-hidden" data-testid={`candidate-group-${key}`}>
                  <CardContent className="p-0">
                    {/* Candidate header - clickable to expand */}
                    <div className="w-full p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors">
                      {/* Group-level select-all checkbox — selects every simulation of this candidate */}
                      {(() => {
                        const state = groupSelectionState(group);
                        return (
                          <input
                            type="checkbox"
                            checked={state === "all"}
                            ref={(el) => { if (el) el.indeterminate = state === "some"; }}
                            onChange={() => toggleGroupSelected(group)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 cursor-pointer accent-[var(--brand-primary)] shrink-0"
                            title={state === "all" ? "Desmarcar todas as simulações deste candidato" : "Selecionar todas as simulações deste candidato"}
                            data-testid={`select-group-${key}`}
                          />
                        );
                      })()}
                      <button
                        type="button"
                        className="flex-1 flex items-center gap-4 text-left"
                        onClick={() => toggleExpand(key, group)}
                        data-testid={`expand-group-${key}`}
                      >
                        <div className={`${scoreColor} w-12 h-12 rounded-lg flex items-center justify-center shrink-0`}>
                          <span className="text-white text-xs font-bold">
                            {(r?.gross_margin_percentage || r?.margin_percentage) != null ? `${(r.gross_margin_percentage || r.margin_percentage).toFixed(1)}%` : "—"}
                          </span>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm truncate">{group.name || "Sem nome"}</span>
                            {group.nif && (
                              <span className="text-xs text-muted-foreground font-mono bg-slate-100 px-1.5 py-0.5 rounded">
                                NIF: {group.nif}
                              </span>
                            )}
                            {group.email && (
                              <span className="text-xs text-muted-foreground font-mono bg-slate-100 px-1.5 py-0.5 rounded">
                                {group.email}
                              </span>
                            )}
                            <span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                              {group.simulations.length} sim.
                            </span>
                            {(latestSim.proposal_links?.length > 0 || proposals.length > 0) && (
                              <span className="text-xs bg-brand-50 text-brand-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                                <LinkIcon className="w-3 h-3" />
                                {latestSim.proposal_links?.length || proposals.length} prop.
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            {(() => {
                              const bt = getBusinessType(latestSim);
                              return (
                                <span className={`px-1.5 py-0.5 rounded border font-medium ${bt.color}`} data-testid={`candidate-type-${bt.key}`}>
                                  {bt.label}
                                </span>
                              );
                            })()}
                            {(() => {
                              const rate = getRateLabel(latestSim);
                              return rate ? (
                                <span data-testid={`candidate-rate-${key}`}>
                                  Taxa: <span className="font-semibold text-foreground">{rate}</span>
                                </span>
                              ) : null;
                            })()}
                            {r?.gross_monthly != null && <span>Bruto: {fmt(r.gross_monthly)}</span>}
                            {(() => {
                              const { liq14, liq12 } = getLiquids(r);
                              return (liq14 != null && liq12 != null) ? (
                                <>
                                  <span>Líq.14: <span className="font-semibold text-foreground">{fmt(liq14)}</span></span>
                                  <span>Líq.12: <span className="font-semibold text-foreground">{fmt(liq12)}</span></span>
                                </>
                              ) : null;
                            })()}
                            {latestSim.updated_at && (
                              <span className="ml-auto">{new Date(latestSim.updated_at).toLocaleDateString("pt-PT")}</span>
                            )}
                          </div>
                        </div>
                      </button>

                      <div className="flex items-center gap-1 shrink-0">
                        {(() => {
                          // Preferir link de proposta assinável (com token). Fallback para link do simulador partilhado.
                          const propToken = latestSim.proposal_links?.[0]?.token || proposals?.[0]?.token;
                          const propUrl = propToken
                            ? `${window.location.origin}/proposal/sign/${propToken}`
                            : (latestSim?.id ? `${window.location.origin}/simulator/shared/${latestSim.id}` : null);
                          if (!propUrl) return null;
                          return (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-[var(--brand-primary)]"
                              onClick={(e) => { e.stopPropagation(); copyLink(propUrl); }}
                              title={propToken ? "Copiar link da proposta" : "Copiar link da simulação"}
                              data-testid={`copy-candidate-link-${key}`}
                            >
                              <LinkIcon className="w-3.5 h-3.5" />
                            </Button>
                          );
                        })()}
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); exportCandidate(group); }} title="Exportar PDF" data-testid={`export-pdf-btn-${group.nif}`}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); openEdit(latestSim); }} title="Editar">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <button
                          type="button"
                          onClick={() => toggleExpand(key, group)}
                          className="h-8 w-8 p-0 flex items-center justify-center rounded hover:bg-slate-100"
                          title={isExpanded ? "Recolher" : "Expandir"}
                          data-testid={`toggle-expand-${key}`}
                        >
                          {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                        </button>
                      </div>
                    </div>

                    {/* Expanded content */}
                    {isExpanded && (
                      <div className="border-t bg-slate-50/50 p-4 space-y-3">
                        {/* Simulations */}
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Simulações ({group.simulations.length})</p>
                          <div className="space-y-1.5">
                            {group.simulations.map(sim => {
                              const sr = getResult(sim);
                              const { liq14: l14, liq12: l12 } = getLiquids(sr);
                              const bt = getBusinessType(sim);
                              const isContract = bt.key === "contract";
                              return (
                                <div key={sim.id} className={`flex items-center gap-3 p-2 bg-white rounded border text-xs ${selectedSimIds.has(sim.id) ? "ring-2 ring-[var(--brand-primary)]" : ""}`}>
                                  <input
                                    type="checkbox"
                                    checked={selectedSimIds.has(sim.id)}
                                    onChange={() => toggleSimSelected(sim.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-4 h-4 cursor-pointer accent-[var(--brand-primary)]"
                                    title="Selecionar"
                                    data-testid={`select-sim-${sim.id}`}
                                  />
                                  <span className={`px-1.5 py-0.5 rounded border font-medium shrink-0 ${bt.color}`}>
                                    {bt.label}
                                  </span>
                                  {(() => {
                                    const rate = getRateLabel(sim);
                                    return rate ? (
                                      <span data-testid={`sim-rate-${sim.id}`}>Taxa: <span className="font-semibold text-foreground">{rate}</span></span>
                                    ) : null;
                                  })()}
                                  <span>Bruto: {fmt(sr?.gross_monthly)}</span>
                                  {isContract && l14 != null && l12 != null ? (
                                    <>
                                      <span>Líq.14: <span className="font-semibold text-foreground">{fmt(l14)}</span></span>
                                      <span>Líq.12: <span className="font-semibold text-foreground">{fmt(l12)}</span></span>
                                    </>
                                  ) : (
                                    <span>Líq: {fmt(sr?.net_monthly)}</span>
                                  )}
                                  <span className="text-muted-foreground">{(sr?.gross_margin_percentage || sr?.margin_percentage)?.toFixed(1)}%</span>
                                  <span className="ml-auto text-muted-foreground">{sim.updated_at ? new Date(sim.updated_at).toLocaleDateString("pt-PT") : ""}</span>
                                  <Button variant="outline" size="sm" className="h-6 px-2 text-[10px] text-[var(--brand-primary)] border-[var(--brand-primary)]" onClick={() => navigate(`/simulator/shared/${sim.id}`)} title="Abrir no Simulador" data-testid={`open-sim-${sim.id}`}>
                                    Abrir
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-[var(--brand-primary)]" onClick={() => generatePdfFromSimulation(sim, group)} title="Gerar PDF" data-testid={`gen-pdf-${sim.id}`}>
                                    <FileText className="w-3 h-3 mr-1" />PDF
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-[var(--brand-primary)]" onClick={() => openLinkDialog(sim, group, key)} title="Criar Link de Proposta" data-testid={`create-link-${sim.id}`}>
                                    <LinkIcon className="w-3 h-3 mr-1" />Link
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => deleteSimulation(sim)} title="Eliminar">
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Proposals */}
                        {proposals.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Propostas ({proposals.length})</p>
                            <div className="space-y-1.5">
                              {[...proposals].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).map(p => {
                                const pUrl = `${window.location.origin}/proposal/sign/${p.token}`;
                                const bt = getBusinessType(p);
                                return (
                                  <div key={p.id} className="flex items-center gap-3 p-2 bg-white rounded border text-xs">
                                    <LinkIcon className="w-3.5 h-3.5 text-[var(--brand-primary)] shrink-0" />
                                    <span className={`px-1.5 py-0.5 rounded border font-medium shrink-0 ${bt.color}`}>
                                      {bt.label}
                                    </span>
                                    <span className={`px-1.5 py-0.5 rounded font-medium shrink-0 ${
                                      p.status === "accepted" ? "bg-brand-100 text-brand-800" :
                                      p.status === "rejected" ? "bg-red-100 text-red-800" :
                                      "bg-amber-100 text-amber-700"
                                    }`}>
                                      {p.status === "accepted" ? "Aceite" : p.status === "rejected" ? "Não Aceite" : "Pendente"}
                                    </span>
                                    {p.rejection_reason && <span className="text-xs text-red-500 truncate max-w-[120px]" title={p.rejection_reason}>&quot;{p.rejection_reason}&quot;</span>}
                                    <span className="font-mono text-muted-foreground truncate flex-1">{pUrl}</span>
                                    {p.signed_at ? (
                                      <span className="flex items-center gap-1 text-brand-600 shrink-0" title="Assinado em">
                                        <Clock className="w-3 h-3" />
                                        {new Date(p.signed_at).toLocaleDateString("pt-PT")}
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1 text-amber-600 shrink-0" title="Enviado em">
                                        <Clock className="w-3 h-3" />
                                        Enviado: {new Date(p.created_at).toLocaleDateString("pt-PT")}
                                      </span>
                                    )}
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyLink(pUrl)} title="Copiar link">
                                      <Copy className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0 text-[var(--brand-primary)] hover:text-brand-700"
                                      onClick={() => openEmailDialog(p, group.email)}
                                      title="Enviar por email (link + PDF anexo)"
                                      data-testid={`send-email-${p.id}`}
                                    >
                                      <Mail className="w-3 h-3" />
                                    </Button>
                                    {p.status === "accepted" && (
                                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-brand-700" onClick={() => downloadProposalPdf(p.id)} title="Download PDF aceite" data-testid={`download-accepted-pdf-${p.id}`}>
                                        <FileText className="w-3 h-3" />
                                      </Button>
                                    )}
                                    <a href={pUrl} target="_blank" rel="noopener noreferrer">
                                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Abrir"><ExternalLink className="w-3 h-3" /></Button>
                                    </a>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Send Email Dialog */}
      <Dialog open={emailDialog} onOpenChange={(o) => { if (!o) { setEmailDialog(false); setEmailProposal(null); } }}>
        <DialogContent className="max-w-md" data-testid="email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[var(--brand-primary)]" />
              Enviar Proposta por Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Email do candidato</Label>
              <Input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="exemplo@email.com"
                className="mt-1"
                data-testid="email-to-input"
                autoFocus
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Vamos enviar o link da proposta + PDF anexo a partir de <span className="font-mono text-foreground">finance@evolutt.io</span>.
              </p>
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-900">
                ⚠️ O email <strong>só será enviado quando clicares em &quot;Enviar&quot;</strong>. Nenhum envio é automático.
              </div>
            </div>
            {emailProposal && (
              <div className="text-xs bg-slate-50 border rounded p-2 space-y-0.5">
                <div><span className="text-muted-foreground">Candidato:</span> <span className="font-medium">{emailProposal.candidate_name}</span></div>
                <div className="truncate"><span className="text-muted-foreground">Link:</span> <span className="font-mono text-[10px]">{window.location.origin}/proposal/sign/{emailProposal.token}</span></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog(false)} disabled={emailSending} data-testid="email-cancel-btn">
              Cancelar
            </Button>
            <Button
              onClick={sendProposalEmail}
              disabled={emailSending || !emailTo}
              className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary)] text-white"
              data-testid="email-send-btn"
            >
              {emailSending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A enviar...</> : <><Mail className="w-4 h-4 mr-2" />Enviar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Link Confirmation Dialog */}
      <Dialog open={linkDialog.open} onOpenChange={(o) => { if (!o) closeLinkDialog(); }}>
        <DialogContent className="max-w-md" data-testid="link-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-[var(--brand-primary)]" />
              Criar Link de Proposta
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <p className="text-muted-foreground">
              Vais criar um link de proposta partilhável para este candidato. O link
              <strong className="text-foreground"> só será guardado quando clicares em &quot;Guardar&quot;</strong>.
              Nenhum email será enviado automaticamente.
            </p>
            {linkDialog.group && (
              <div className="text-xs bg-slate-50 border rounded p-2 space-y-0.5">
                <div><span className="text-muted-foreground">Candidato:</span> <span className="font-medium">{linkDialog.group.name || "Sem nome"}</span></div>
                {linkDialog.group.nif && <div><span className="text-muted-foreground">NIF:</span> <span className="font-mono">{linkDialog.group.nif}</span></div>}
                {linkDialog.group.email && <div className="truncate"><span className="text-muted-foreground">Email:</span> <span className="font-mono">{linkDialog.group.email}</span></div>}
                {linkDialog.sim && (() => {
                  const rate = getRateLabel(linkDialog.sim);
                  const sr = getResult(linkDialog.sim);
                  return (
                    <>
                      {rate && <div><span className="text-muted-foreground">Taxa:</span> <span className="font-medium">{rate}</span></div>}
                      {sr?.gross_monthly != null && <div><span className="text-muted-foreground">Bruto:</span> <span className="font-medium">{fmt(sr.gross_monthly)}</span></div>}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeLinkDialog} disabled={linkDialog.saving} data-testid="link-cancel-btn">
              Cancelar
            </Button>
            <Button
              onClick={confirmCreateProposalLink}
              disabled={linkDialog.saving}
              className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary)] text-white"
              data-testid="link-save-btn"
            >
              {linkDialog.saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A guardar...</> : <><LinkIcon className="w-4 h-4 mr-2" />Guardar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Candidate Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Editar Candidato</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-xs">Nome</Label><Input value={editForm.name} onChange={(e) => updateEditForm({ ...editForm, name: e.target.value })} className="mt-1" data-testid="edit-candidate-name" /></div>
            <div><Label className="text-xs">NIF</Label><Input value={editForm.nif} onChange={(e) => updateEditForm({ ...editForm, nif: e.target.value })} className="mt-1" data-testid="edit-candidate-nif" /></div>
            <div><Label className="text-xs">Data de Nascimento</Label><Input type="date" value={editForm.birth_date} onChange={(e) => updateEditForm({ ...editForm, birth_date: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Notas</Label><Input value={editForm.notes} onChange={(e) => updateEditForm({ ...editForm, notes: e.target.value })} className="mt-1" placeholder="Notas opcionais..." /></div>
          </div>
          <DialogFooter>
            <div className="flex items-center gap-2 mr-auto text-xs">
              {saving && <><Loader2 className="w-3 h-3 animate-spin text-muted-foreground" /><span className="text-muted-foreground">A guardar...</span></>}
              {saved && !saving && <><Check className="w-3 h-3 text-brand-600" /><span className="text-brand-600">Guardado</span></>}
            </div>
            <Button variant="outline" onClick={() => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); setEditDialog(false); }}>Fechar</Button>
            <Button onClick={saveCandidate} disabled={saving} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary)] text-white" data-testid="save-candidate-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CandidateLinks;
